import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

function Error({ error, onReset }) {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1E40AF] to-[#1E3A8A] text-white p-8">
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto"
      >
        <div className="glass-card p-8 rounded-modern text-center">
          <div className="mb-8">
            <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto" />
          </div>

          <h1 className="text-4xl font-bold mb-4 text-gradient">Oops! Something went wrong</h1>
          
          <p className="text-lg mb-8 text-[#6B7280]">
            {error || "We encountered an error while processing your request."}
          </p>

          <div className="flex flex-wrap gap-4 justify-center mb-8">
            <button
              onClick={onReset}
              className="px-6 py-3 bg-gradient-to-r from-[#2563EB] to-[#4F46E5] rounded-modern hover:from-[#1E40AF] hover:to-[#4338CA] transition-colors flex items-center space-x-2"
            >
              <ArrowPathIcon className="w-5 h-5" />
              <span>Try Again</span>
            </button>

            <button
              onClick={() => navigate('/contact')}
              className="px-6 py-3 bg-white/10 rounded-modern hover:bg-white/20 transition-colors flex items-center space-x-2"
            >
              <QuestionMarkCircleIcon className="w-5 h-5" />
              <span>Get Help</span>
            </button>
          </div>

          <div className="border-t border-white/10 pt-8">
            <h2 className="text-xl font-bold mb-4 text-gradient">Troubleshooting Tips</h2>
            <ul className="text-left space-y-4 text-[#6B7280]">
              <li className="flex items-start space-x-3">
                <span className="text-[#4F46E5]">•</span>
                <span>Check if your image is in a supported format (JPG, PNG)</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-[#4F46E5]">•</span>
                <span>Ensure your image is less than 10MB in size</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-[#4F46E5]">•</span>
                <span>Make sure you have a stable internet connection</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-[#4F46E5]">•</span>
                <span>If the problem persists, please contact our support team</span>
              </li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default Error;
